import React from 'react';
import {
  SearchOutlined,
  ExperimentOutlined,
  BugOutlined,
  UserOutlined,
  FileTextOutlined,
  ClusterOutlined,
  TeamOutlined,
  BankOutlined,
  FileSearchOutlined,
  DashboardOutlined,
  BellOutlined,
  WarningOutlined,
  ScheduleOutlined,
  NotificationOutlined,
  ApartmentOutlined,
  LockOutlined,
  BookOutlined,
  RiseOutlined,
  FormOutlined,
  BarChartOutlined,
  ApiOutlined,
  SafetyCertificateOutlined,
  NumberOutlined,
  MailOutlined,
  AppstoreOutlined,
  MessageOutlined,
  ShopOutlined,
  StarOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  ACCESS_CATALOG_STATES,
  getAccessCatalogState,
  isModuleKeyLive,
} from '../../assets/utils/accessCatalog';
import { isRoutablePath, routePathname } from './appRoutes';

// Feature flags (build-time via CRA env)
export const TARIFF_FEATURE_ENABLED =
  process.env.REACT_APP_TARIFF_FEATURE_ENABLED === '1' ||
  process.env.REACT_APP_TARIFF_FEATURE_ENABLED === 'true';

// The legacy Plans module is superseded by Plan Management and hidden from the
// sidebar ahead of being decommissioned. Its routes still resolve, so existing
// links and deep links keep working — set REACT_APP_LEGACY_PLANS_NAV=1 to show
// the entry again.
export const LEGACY_PLANS_NAV_ENABLED =
  process.env.REACT_APP_LEGACY_PLANS_NAV === '1' ||
  process.env.REACT_APP_LEGACY_PLANS_NAV === 'true';

// Modules kept out of the sidebar. Applied to the hardcoded items AND to the
// access catalog, which can otherwise contribute a nav item of its own for a
// module key the static config does not carry.
export const HIDDEN_NAV_MODULE_KEYS = new Set(LEGACY_PLANS_NAV_ENABLED ? [] : ['plans']);

/**
 * Single source of truth for the sidebar nav tree and the routes it maps to.
 * A function (not a static export) so every consumer gets fresh item objects —
 * Sidebar.js appends dynamic access-catalog items per render and must not
 * mutate a shared array.
 */
export const buildNavigationConfig = () => [
  {
    group: 'Dashboard',
    items: [
      {
        key: 'dashboardOverview',
        label: 'Dashboard (Overview)',
        icon: <DashboardOutlined />,
        path: '/dashboard',
        module: 'dashboard',
      },
      {
        key: 'dashboardPreauth',
        label: 'PA Dashboard',
        icon: <BarChartOutlined />,
        path: '/dashboard/preauth',
        module: 'preauthAnalytics',
      },
      {
        key: 'dashboardEnrollees',
        label: 'Enrollees Dashboard',
        icon: <TeamOutlined />,
        path: '/dashboard/enrollees',
        module: 'dashboard',
        children: [
          {
            key: 'formSettings',
            label: 'Form settings',
            icon: <FormOutlined />,
            path: '/admin/form-settings',
            module: 'formSettings',
          },
        ],
      },
      {
        key: 'search',
        label: 'Search',
        icon: <SearchOutlined />,
        path: '/search',
        module: 'search',
      },
      {
        key: 'notificationsCenter',
        label: 'Notifications',
        icon: <BellOutlined />,
        path: '/notifications',
        module: 'notificationsCenter',
      },
    ],
  },
  {
    group: 'Client Management',
    items: [
      {
        key: 'client',
        label: 'Client Verification',
        icon: <UserOutlined />,
        path: '/verify',
        module: 'clientVerification',
        badge: true,
      },
      {
        key: 'onboarding-otp',
        label: 'OTP Lookup',
        icon: <SafetyCertificateOutlined />,
        path: '/admin/onboarding-otp',
        module: 'onboardingOtp',
      },
      {
        key: 'appointments',
        label: 'Appointments',
        icon: <ScheduleOutlined />,
        path: '/admin/appointments',
        module: 'appointments',
        badge: true,
      },
      {
        key: 'encounters',
        label: 'Encounters',
        icon: <FormOutlined />,
        path: '/checkins',
        module: 'encounters',
        children: [
          {
            key: 'checkinCleanup',
            label: 'Soft-deleted check-ins',
            icon: <WarningOutlined />,
            path: '/admin/soft-deleted-checkins',
            module: 'checkinCleanup',
          },
          {
            key: 'facilitySummary',
            label: 'Facility summary',
            icon: <BarChartOutlined />,
            path: '/checkins/facility-summary',
            module: 'encounters',
          },
        ],
      },
    ],
  },
  {
    group: 'Operations',
    items: [
      {
        key: 'preauth',
        label: 'Pre-Authorization',
        icon: <FileTextOutlined />,
        path: '/preauth',
        module: 'preauth',
        badge: true,
      },
      {
        key: 'claims',
        label: 'Claims Processing',
        icon: <ClusterOutlined />,
        path: '/claims',
        module: 'claims',
        badge: true,
        children: [
          {
            // Spec §8. Breadcrumb / parent-highlight only — reached from the
            // Claims module strip (ModuleNav MODULE_NAVS.claims), never shown
            // as a sidebar sub-item.
            key: 'pendingSubmissions',
            label: 'Pending submission',
            icon: <ClusterOutlined />,
            path: '/claims/pending-submissions',
            module: 'claims',
          },
        ],
      },
    ],
  },
  {
    group: 'Administration',
    items: [
      {
        key: 'orgs',
        label: 'Organizations',
        icon: <BankOutlined />,
        path: '/corporations',
        module: 'organizations',
        badge: true,
      },
      {
        key: 'facilities',
        label: 'Facilities',
        icon: <ApartmentOutlined />,
        path: '/facilities',
        module: 'facilities',
        children: [
          {
            key: 'facilityTypes',
            label: 'Facility types',
            icon: <ShopOutlined />,
            path: '/admin/settings/facility-types',
            module: 'facilityTypes',
          },
          {
            key: 'facilityRatings',
            label: 'Facility ratings',
            icon: <StarOutlined />,
            path: '/admin/facility-ratings',
            module: 'facilityRatings',
          },
          {
            key: 'helpContent',
            label: 'Portal help & tutorials',
            icon: <QuestionCircleOutlined />,
            path: '/admin/help-content',
            module: 'helpContent',
          },
        ],
      },
      {
        key: 'plans',
        label: 'Plans',
        icon: <BookOutlined />,
        path: '/plans',
        module: 'plans',
        badge: true,
      },
      // Tariff Management V2 (feature-flagged)
      ...(TARIFF_FEATURE_ENABLED
        ? [
            {
              key: 'tariffsV2',
              label: 'Standard tariff',
              icon: <RiseOutlined />,
              path: '/admin/tariffs',
              module: 'tariffs',
            },
          ]
        : []),
    ],
  },
  {
    group: 'System',
    items: [
      {
        key: 'staffs',
        label: 'Staff Management',
        icon: <TeamOutlined />,
        path: '/dash',
        module: 'staffManagement',
      },
      {
        key: 'rbacAdmin',
        label: 'RBAC Admin',
        icon: <LockOutlined />,
        path: '/rbac',
        module: 'rbacAdmin',
      },
      {
        key: 'platformContexts',
        label: 'Platform contexts',
        icon: <SafetyCertificateOutlined />,
        path: '/platform/contexts',
        permission: 'platform.*',
      },
      {
        key: 'identityGenerationSettings',
        label: 'Identity generation settings',
        icon: <NumberOutlined />,
        path: '/platform/identity-generation-settings',
        permission: 'platform.*',
      },
      {
        key: 'integrationPartners',
        label: 'Partners',
        icon: <ApiOutlined />,
        path: '/admin/integrations/partners',
        module: 'integrationsSaaSPro',
      },
      {
        key: 'campaigns',
        label: 'Communications',
        icon: <NotificationOutlined />,
        path: '/admin/campaigns',
        module: 'broadcastNotifications',
        children: [
          {
            key: 'campaignsPush',
            label: 'Push notifications',
            icon: <MessageOutlined />,
            path: '/admin/campaigns?channel=push',
            module: 'broadcastNotifications',
          },
          {
            key: 'campaignsEmail',
            label: 'Email notifications',
            icon: <MailOutlined />,
            path: '/admin/campaigns?channel=email',
            module: 'broadcastNotifications',
          },
          {
            key: 'announcements',
            label: 'Announcements',
            icon: <NotificationOutlined />,
            path: '/admin/announcements',
            module: 'announcements',
          },
          {
            key: 'facilityNotices',
            label: 'Facility notices',
            icon: <NotificationOutlined />,
            path: '/admin/facility-notices',
            module: 'facilityNotices',
          },
        ],
      },
      {
        key: 'opsEmailRecipients',
        label: 'Ops email recipients',
        icon: <MailOutlined />,
        path: '/admin/settings/notifications',
        module: 'opsEmailRecipients',
      },
      {
        key: 'notificationSystem',
        label: 'Notification system',
        icon: <NotificationOutlined />,
        path: '/admin/settings/notification-system',
        module: 'notificationSystem',
      },
      {
        key: 'smsProviderSettings',
        label: 'SMS provider',
        icon: <MessageOutlined />,
        path: '/admin/settings/sms-provider',
        module: 'smsProviderSettings',
      },
      {
        key: 'testSubjects',
        label: 'Test subjects',
        icon: <BugOutlined />,
        path: '/admin/settings/test-subjects',
        module: 'testSubjects',
      },
      {
        key: 'planManagement',
        label: 'Plan Management',
        icon: <ExperimentOutlined />,
        path: '/admin/plan-management',
        module: 'planManagement',
      },
      {
        key: 'log',
        label: 'Activity Log',
        icon: <FileSearchOutlined />,
        path: '/log',
        module: 'activityLog', // requires logs.read or admin.* (platform.* implies admin.*)
      },
    ],
  },
];

/** Icon used for dynamically-injected access-catalog items not in the static tree above. */
export const CatalogItemIcon = AppstoreOutlined;

/** The only application whose Access Modules rows this app may render. */
const CATALOG_APPLICATION = 'care-admin';

/** Catalog rows whose routes this build cannot reach — warned about once per key. */
const warnedDeadRouteKeys = new Set();

/** Read one access-module row (API-normalised or built-in) into the fields the nav needs. */
const readCatalogRow = (mod) => {
  const key = String(mod?.key || '');
  const label = typeof mod?.label === 'string' ? mod.label.trim() : '';
  const group = typeof mod?.group === 'string' ? mod.group.trim() : '';
  const sortOrder = Number(mod?.sort_order);
  return {
    key,
    label,
    group,
    application: mod?.application,
    routes: Array.isArray(mod?.routes) ? mod.routes : [],
    // Built-in rows omit these flags entirely; only an explicit `false` hides a module,
    // which is exactly how the sidebar has always read them.
    navVisible: mod?.nav_visible !== false,
    isActive: mod?.is_active !== false,
    sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
  };
};

/**
 * Build the sidebar/breadcrumb tree with the backend access-module catalog as the source
 * of truth, and the hardcoded tree above as the bootstrap fallback.
 *
 * Rules, in the order they matter:
 *
 * 1. Every hardcoded item is kept. A module the live catalog has no row for renders exactly
 *    as it does in the tree above — the catalog can be incomplete, and omission is never
 *    read as "hide this". Only an explicit `nav_visible: false` / `is_active: false` on a
 *    live row removes an item, because that is an operator decision, not a gap.
 * 2. When the catalog state is not `live` (offline, never fetched, or genuinely empty) no
 *    row is treated as authoritative, so the result is the hardcoded tree plus the same
 *    extra rows the sidebar has always appended — unchanged behaviour on the degraded path.
 * 3. Presentation the backend does not own stays local: icons always come from the tree
 *    above (CatalogItemIcon for catalog-only modules), and hardcoded items keep their own
 *    `path`, `badge`, and `key` so routing, badges, and pinned items are untouched.
 * 4. A row naming no route this build can reach never becomes an item (see appRoutes.js).
 * 5. Ordering is `sort_order` first, hardcoded position as the tie-break — so a catalog that
 *    expresses no opinion (every row 0) reproduces the tree above exactly.
 *
 * Permission gating is not done here: callers still gate each item with canStayOnModule.
 *
 * @param {object} [options]
 * @param {Array} [options.accessModules] rows from the auth context (live or built-in)
 * @param {'live'|'empty'|'unavailable'} [options.catalogState]
 * @returns {Array<{ group: string, items: Array<object> }>}
 */
export const buildNavigationTree = ({
  accessModules = [],
  catalogState = getAccessCatalogState(),
  isLiveKey = isModuleKeyLive,
  isRoutable = isRoutablePath,
} = {}) => {
  const rowsByKey = new Map();
  (Array.isArray(accessModules) ? accessModules : []).forEach((mod) => {
    const row = readCatalogRow(mod);
    if (row.key && !rowsByKey.has(row.key)) rowsByKey.set(row.key, row);
  });

  const catalogIsLive = catalogState === ACCESS_CATALOG_STATES.LIVE;

  // A row is authoritative only when the API actually returned it for THIS app. A built-in
  // standing in for a missing row, or a row registered against another application, leaves
  // the hardcoded item exactly as it is.
  const authoritativeRow = (moduleKey) => {
    if (!moduleKey || !catalogIsLive || !isLiveKey(moduleKey)) return null;
    const row = rowsByKey.get(moduleKey);
    if (!row) return null;
    if (row.application && row.application !== CATALOG_APPLICATION) return null;
    return row;
  };

  // Items can carry `children`: pages that belong to the parent's module (Facility types
  // inside Facilities, Announcements inside Campaigns, …). They are NOT sidebar items —
  // the main navigation stays one entry per module and each module page reaches them
  // through its own in-module strip (components/ModuleNav). Children exist in the tree
  // so the breadcrumb reads Group / Parent / Page, the parent stays lit while a child is
  // open, and the catalog can still rename or hide each one by its own module. They are
  // flattened here and re-nested at the end so a child always sits under its parent.
  const staticEntries = [];
  buildNavigationConfig().forEach((group) => {
    group.items.forEach((item) => {
      const { children, ...parent } = item;
      staticEntries.push({ item: parent, group: group.group, seq: staticEntries.length });
      (children || []).forEach((childItem) => {
        staticEntries.push({
          item: { ...childItem, parentKey: item.key },
          group: group.group,
          seq: staticEntries.length,
        });
      });
    });
  });

  // A catalog row carries ONE label/group, but a module key can own several nav items
  // (e.g. `dashboard` owns both "Dashboard (Overview)" and "Enrollees Dashboard"). Only the
  // primary item — the one sitting on the row's first route, else the first item declared —
  // takes the row's label/group/order; the others keep their hardcoded presentation.
  const primaryByModule = new Map();
  staticEntries.forEach((entry) => {
    const moduleKey = entry.item.module;
    if (!moduleKey) return;
    const current = primaryByModule.get(moduleKey);
    if (!current) {
      primaryByModule.set(moduleKey, entry);
      return;
    }
    const row = authoritativeRow(moduleKey);
    const firstRoute = row ? routePathname(row.routes[0]) : '';
    if (
      firstRoute
      && routePathname(entry.item.path) === firstRoute
      && routePathname(current.item.path) !== firstRoute
    ) {
      primaryByModule.set(moduleKey, entry);
    }
  });

  const entries = [];
  const usedPaths = new Set();

  staticEntries.forEach((entry) => {
    const moduleKey = entry.item.module;
    if (moduleKey && HIDDEN_NAV_MODULE_KEYS.has(moduleKey)) return;
    const row = authoritativeRow(moduleKey);
    // Hiding is per module, not per item: switching a module off in RBAC Admin takes every
    // nav item that module owns with it.
    if (row && (!row.navVisible || !row.isActive)) return;
    const isPrimary = Boolean(row) && primaryByModule.get(moduleKey) === entry;
    // A child follows its parent wherever the catalog puts the parent; only a top-level
    // item takes a group from its own row.
    const parentEntry = entry.item.parentKey
      ? entries.find((candidate) => candidate.key === entry.item.parentKey)
      : null;
    entries.push({
      ...entry.item,
      label: isPrimary && row.label ? row.label : entry.item.label,
      groupName: parentEntry
        ? parentEntry.groupName
        : (isPrimary && row.group ? row.group : entry.group),
      order: parentEntry ? parentEntry.order : (isPrimary ? row.sortOrder : 0),
      seq: entry.seq,
    });
    usedPaths.add(routePathname(entry.item.path));
  });

  const staticModuleKeys = new Set(
    staticEntries.map((entry) => entry.item.module).filter(Boolean),
  );

  let catalogSeq = staticEntries.length;
  rowsByKey.forEach((row) => {
    if (staticModuleKeys.has(row.key)) return;
    if (HIDDEN_NAV_MODULE_KEYS.has(row.key)) return;
    if (row.application !== CATALOG_APPLICATION) return;
    if (!row.navVisible || !row.isActive) return;

    const path = row.routes.map(routePathname).find((candidate) => isRoutable(candidate));
    if (!path) {
      if (catalogIsLive && row.routes.length && !warnedDeadRouteKeys.has(row.key)) {
        warnedDeadRouteKeys.add(row.key);
        // eslint-disable-next-line no-console
        console.warn(
          `[navigation] Access module "${row.key}" is not in the sidebar: this build has no `
          + `page at ${row.routes.join(', ')}. A nav item pointing there would bounce the user `
          + 'to the login screen, so the row is skipped until the route ships.'
        );
      }
      return;
    }
    if (usedPaths.has(path)) return;
    usedPaths.add(path);

    catalogSeq += 1;
    entries.push({
      key: `catalog-${row.key}`,
      label: row.label || row.key,
      icon: <CatalogItemIcon />,
      path,
      module: row.key,
      groupName: row.group || 'Other',
      order: row.sortOrder,
      seq: catalogSeq,
    });
  });

  const byOrderThenPosition = (a, b) => (a.order - b.order) || (a.seq - b.seq);

  const grouped = new Map();
  entries.forEach((entry) => {
    const name = entry.groupName || 'Other';
    if (!grouped.has(name)) grouped.set(name, []);
    grouped.get(name).push(entry);
  });

  // Re-nest: a child whose parent survived goes under it; one whose parent the catalog
  // hid stays visible at the top level of its group rather than vanishing with it.
  const nest = (groupEntries) => {
    const parents = new Map();
    groupEntries.forEach((entry) => {
      if (!entry.parentKey) parents.set(entry.key, entry);
    });
    const topLevel = [];
    groupEntries.forEach((entry) => {
      const parent = entry.parentKey ? parents.get(entry.parentKey) : null;
      if (parent) {
        if (!parent.children) parent.children = [];
        parent.children.push(entry);
      } else {
        topLevel.push(entry);
      }
    });
    return topLevel;
  };

  const strip = ({ groupName, order, seq, parentKey, children, ...item }) => (
    children ? { ...item, children: children.map(strip) } : item
  );

  return Array.from(grouped.entries())
    .map(([name, groupEntries]) => {
      const sorted = [...groupEntries].sort(byOrderThenPosition);
      return { group: name, entries: sorted, lead: sorted[0] };
    })
    .sort((a, b) => byOrderThenPosition(a.lead, b.lead))
    .map(({ group, entries: groupEntries }) => ({
      group,
      items: nest(groupEntries).map(strip),
    }));
};

/** Every item in a tree, parents followed by their children — for lookups and active-route checks. */
export const flattenNavItems = (items = []) => items.flatMap((item) => (
  item.children && item.children.length
    ? [item, ...flattenNavItems(item.children)]
    : [item]
));
